import { Flex, FlexProps, Heading, HStack, UseDisclosureReturn } from '@chakra-ui/react';
import {
  BaseButton,
  BaseCard,
  BaseDialog,
  BaseLoadingBar,
  useHookForm,
  useStandardToast
} from '@cks-internal/react-base';
import { CreateActivityModel } from '../../../features/activities/editActivity/editActivityForm/schema/editActivitySchema';
import ActivityStatus from '../../../enums/activities/ActivityStatus';
import ActivityAlertLevel from '../../../enums/activities/ActivityAlertLevel';
import { useEffect, useState } from 'react';
import { getReportingRequirementsInitialEditModel } from '../../../features/activities/editActivity/editReportingRequirements/utils';
import {
  ActivityReportingRequirementsViewModel,
  UpdateActivityReportingRequirementsModel
} from '../../../features/activities/editActivity/editReportingRequirements/types';
import { useNewActivityReportingRequirementsModelQuery } from '../../../features/activities/editActivity/editReportingRequirements/api/useNewActivityReportingRequirementsModelQuery';
import { ReportingRequirementsForm } from '../../../features/activities/editActivity/editReportingRequirements/components/reportingRequirementsForm';
import { useNewActivityMutation } from '../../../features/activities/editActivity/editActivityForm/api/useNewActivityMutation';
import { NewActivityForm } from '../../../features/activities/editActivity/editActivityForm/newActivityForm';
import { ActivityNewEditResponse } from '../../../features/activities/editActivity/editActivityForm/api/types';
import { useGetUpdatedNewReportingReqMutation } from '../../../features/activities/editActivity/editReportingRequirements/api/useGetUpdatedNewReportingReqMutation';
import { useFieldArray, useWatch } from 'react-hook-form';
import { HotelBookingType } from '../../../globalModels/common/hotelBookingType';
import { CompanyProjectNotification } from '../../../features/activities/companyProjects/CompanyProjectNotification.tsx';

import { useNavigate } from 'react-router-dom';
import applicationRoutes from '../../../constants/applicationRoutes.ts';
import { useForceOpenActivityStore } from '../../../features/activities/stores/activityTabStore';
import { ActivityInformationTabs } from '../informationDisplays/activityInformationTabs';

type props = {
  disclosure: UseDisclosureReturn;
};

export const NewActivityDialog = (props: props) => {
  const newActivityModel: Partial<CreateActivityModel> = {
    Status: ActivityStatus.Open,
    Priority: ActivityAlertLevel.Normal,
    TravelerIds: [],
    FormsOfPayment: [],
    GuestTravelerNames: [],
    NumberOfRooms: 1
  };

  const { formMethods } = useHookForm<Partial<CreateActivityModel>>(newActivityModel, []);
  const navigate = useNavigate();
  const newActivityMutation = useNewActivityMutation();
  const updateReportingReqModelMutation = useGetUpdatedNewReportingReqMutation();
  const [
    companyId,
    travellerIds,
    approvedBookerId,
    requestedById,
    preferredHotelId,
    hotelBookingType,
    cityId,
    checkInDate,
    checkOutDate
  ] = useWatch({
    control: formMethods.control,
    name: [
      'CompanyId',
      'TravelerIds',
      'ApprovedBookerId',
      'RequestedById',
      'PreferredHotelId',
      'HotelBookingType',
      'PreferredCityId',
      'CheckInDate',
      'CheckOutDate'
    ]
  });

  const [formState, setFormState] = useState<UpdateActivityReportingRequirementsModel | undefined>(undefined);
  const [viewModel, setViewModel] = useState<ActivityReportingRequirementsViewModel>({
    TravellerReportingRequirements: [],
    CompanyReportingRequirements: []
  });

  const [_, setIsValid] = useState(true);
  const toast = useStandardToast();

  const reportingRequirementsModelQueryInitial = useNewActivityReportingRequirementsModelQuery(companyId, []);

  useEffect(() => {
    const data = reportingRequirementsModelQueryInitial.data;
    setFormState(getReportingRequirementsInitialEditModel(data));
    if (data) {
      setViewModel(data);
    }
  }, [reportingRequirementsModelQueryInitial.data]);

  const reportingReqForm = useHookForm<UpdateActivityReportingRequirementsModel>(formState);
  const companyFieldSet = useFieldArray({
    control: reportingReqForm.formMethods.control,
    name: 'CompanyReportingRequirements'
  });

  const travellerFieldSet = useFieldArray({
    control: reportingReqForm.formMethods.control,
    name: 'TravellerReportingRequirements'
  });
  //this is a total hack show sorry.
  useEffect(() => {
    if (companyId) {
      updateReportingReqModelMutation.mutate(
        {
          companyId: companyId!,
          travellerIds: travellerIds ?? [],
          currentModel: reportingReqForm.formMethods.getValues()
        },
        {
          onSuccess: (data) => {
            setViewModel(data.data);
            setFormState(getReportingRequirementsInitialEditModel(data.data));
          }
        }
      );
    }
  }, [companyId, travellerIds]);

  useEffect(() => {
    setViewModel({ CompanyReportingRequirements: [], TravellerReportingRequirements: [] });
    setFormState({ CompanyReportingRequirements: [], TravellerReportingRequirements: [] });
    formMethods.setValue('TravelerIds', []);
  }, [companyId]);

  useEffect(() => {
    if (!props.disclosure.open) {
      formMethods.reset();
      reportingReqForm.formMethods.reset();
    }
  }, [props.disclosure.open]);

  const save = async (onValidSave: (model: ActivityNewEditResponse) => void) => {
    const isActivityFormValid = await formMethods.trigger();
    const isReportingReqFormValid = await reportingReqForm.formMethods.trigger();
    setIsValid(isActivityFormValid && isReportingReqFormValid);
    if (isReportingReqFormValid && isActivityFormValid) {
      await newActivityMutation
        .mutateAsync({
          ActivityModel: formMethods.getValues(),
          ReportingRequirementsModel: reportingReqForm.formMethods.getValues()
        })
        .then((responseData) => {
          toast.successToast('Activity Created');
          onValidSave(responseData.data);
        })
        .catch((e) => {
          alert(e);
        });
    }
  };

  const onSave = async () => {
    await save(() => {
      props.disclosure.onClose();
    });
  };

  const onSaveAndNew = async () => {
    await save(() => {
      // Reset both forms to initial state for creating a new activity
      formMethods.reset(newActivityModel);
      reportingReqForm.formMethods.reset();
      setViewModel({ CompanyReportingRequirements: [], TravellerReportingRequirements: [] });
      setFormState({ CompanyReportingRequirements: [], TravellerReportingRequirements: [] });
    });
  };

  const forceOpenStore = useForceOpenActivityStore();
  const onSaveAndOpen = async () => {
    await save((r) => {
      if (location.pathname.includes(`${applicationRoutes.agent.inbox}`)) {
        forceOpenStore.forceOpenActivity(r.ActivityId);
        props.disclosure.onClose();
        return;
      }
      const navPromise = navigate(`${applicationRoutes.agent.inbox}?tab=${r.ActivityId}`);
      if (typeof navPromise == 'object') {
        navPromise.then(() => {
          props.disclosure.onClose();
        });
        return;
      }
      props.disclosure.onClose();
    });
  };

  useEffect(() => {
    formMethods.trigger().then();
    reportingReqForm.formMethods.trigger().then();
  }, []);

  const saveButtons = (flexProps?: FlexProps) => {
    return (
      <Flex gap={'4px'} mr={'10px'} {...flexProps}>
        <BaseButton
          onClick={onSaveAndOpen}
          variant={'outline'}
          colorScheme={'secondary'}
          marginBottom={'4px'}
          isLoading={newActivityMutation.isPending}>
          Create & Open
        </BaseButton>

        <BaseButton
          onClick={onSaveAndNew}
          variant={'secondary'}
          marginBottom={'4px'}
          isLoading={newActivityMutation.isPending}>
          Create & New
        </BaseButton>

        <BaseButton onClick={onSave} marginBottom={'4px'} isLoading={newActivityMutation.isPending}>
          Create Activity
        </BaseButton>
      </Flex>
    );
  };

  return (
    <BaseDialog
      disclosure={props.disclosure}
      dragDialog={{ initialH: 800, initialW: 1500 }}
      header={
        <Heading m={0} p={0} pb={'14px'}>
          New Activity
        </Heading>
      }
      footerRender={
        <>
          <HStack justifyContent={'space-between'}>{saveButtons()}</HStack>
          <BaseLoadingBar isLoading={newActivityMutation.isPending} />
        </>
      }>
      <Flex gap={'4px'}>
        <Flex direction={'column'} width={'50%'}>
          <CompanyProjectNotification
            cityIds={cityId ? [cityId] : []}
            companyId={companyId}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
          />
          <BaseCard width={'100%'} header={' '}>
            <NewActivityForm
              activityId={undefined}
              data={undefined}
              isDisabled={newActivityMutation.isPending}
              formMethods={formMethods}
            />
            {hotelBookingType != HotelBookingType.Personal && (
              <ReportingRequirementsForm
                formMethods={reportingReqForm.formMethods}
                companyFieldArray={companyFieldSet}
                travellerFieldArray={travellerFieldSet}
                isDisabled={newActivityMutation.isPending}
                companyId={companyId}
                viewModel={viewModel}
                hasBookedHotel={false}
                activityStatus={undefined}
              />
            )}
          </BaseCard>
        </Flex>
        <BaseCard width={'50%'} header={'Activity Information'}>
          <ActivityInformationTabs
            companyId={companyId}
            travellerIds={travellerIds}
            requestedById={requestedById}
            approvedBookerId={approvedBookerId}
            hotelIds={preferredHotelId ? [preferredHotelId] : undefined}
            hotelBookingType={hotelBookingType}
          />
        </BaseCard>
      </Flex>
    </BaseDialog>
  );
};
